import { useState } from 'react';
import {
  Card,
  CardHeader,
  EmptyState,
  FlowBar,
  StatTile,
  ErrorState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  formatNumber,
} from '@mgdlead/ui';
import { Radio } from 'lucide-react';

import { PageHeader } from '@mgdlead/ui';
import { FormAlert } from '@mgdlead/ui';
import { DailyBars } from './DailyBars';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import { humanizeError } from '../../../lib/errors';
import {
  useAnalyticsBreakdown,
  useAnalyticsOverview,
  type AnalyticsOverview,
} from '../../../lib/api';
import { CampaignTotals } from '../../../features/campaigns/CampaignPerformance';
import { AtualizacaoDosDados, FunilDoPeriodo, HojeHero, PrecisaDeAtencao } from './DashboardHoje';
import { workspacePath } from '../../../features/workspace/navigation';
import { Link } from 'react-router';
import { MetricTile, PeriodFilter } from './AnalyticsShell';
import { LucroPorLeadPanel } from './LucroPorLeadPanel';
import { EvasaoCard } from '../../../features/retention/EvasaoCard';
import { RegiaoDoPublico } from '../../../features/regiao/RegiaoDoPublico';
import { ChartCard } from './charts';
import {
  diaLongo,
  diasEmTexto,
  rotuloDoPeriodo,
  type Intervalo,
  type Periodo,
  type PeriodoId,
} from './analytics-ui';

/**
 * O painel diário.
 *
 * ### Por que a métrica de topo é SALDO, e não retenção
 *
 * `docs/product/metricas.md` §4 explica com o dado real: na amostra, a permanência teve
 * mediana de **44 dias**, mínimo de 20 e nenhuma saída antes do oitavo dia. `R(d,1)` daria
 * ~100% todo santo dia — um número que nunca se mexe não informa nada e ainda ocupa o lugar
 * mais valioso da tela. Retenção é leitura de COORTE, e mora no Histórico, em outra cadência.
 *
 * ### Nada aqui é calculado
 *
 * Saldo, cobertura, mediana de permanência e a variação contra o período anterior chegam
 * prontos de `analytics.overview` (invariante 3). Esta tela escolhe rótulo, cor e vazio.
 */
export function DashboardPage() {
  const { workspace } = useWorkspace();
  /*
   * Abre SEMPRE em "Hoje" (pedido do dono, 06/10): é a pergunta de quem abre o painel. Outro
   * recorte é escolha da hora — não fica guardado entre visitas.
   */
  const [preset, setPreset] = useState<PeriodoId>('HOJE');
  const [intervalo, setIntervalo] = useState<Intervalo | null>(null);
  const periodo: Periodo = intervalo ?? preset;
  useDocumentTitle(`Dashboard · ${workspace.name}`);

  const overview = useAnalyticsOverview(workspace.id, periodo);

  return (
    <>
      {/*
        Composição do UI kit do export (out/2026): período nas ações do cabeçalho, o fuso na
        descrição (continua escrito na tela — "hoje" depende dele), KPIs logo abaixo.
      */}
      <PageHeader
        title="Dashboard"
        description={`Entradas, saídas e saldo em ${workspace.name}.`}
        action={
          <PeriodFilter
            value={preset}
            intervalo={{ value: intervalo, onChange: setIntervalo }}
            onChange={setPreset}
            timezone={workspace.timezone}
            inline
          />
        }
      />

      <AtualizacaoDosDados />
      <HojeHero />
      <PrecisaDeAtencao periodo={periodo} />
      <div className="mb-3">
        <EvasaoCard />
      </div>
      <CampanhasNoPeriodo periodo={periodo} />

      {overview.isError ? (
        <ErrorState
          title="Não foi possível carregar os números"
          description={humanizeError(overview.error)}
          onRetry={() => void overview.refetch()}
        />
      ) : (
        <DashboardConteudo data={overview.data} loading={overview.isPending} periodo={periodo} />
      )}

      {/* De onde é o público, pelo DDD (pedido do dono, 11/10) — depois dos gráficos principais. */}
      <div className="mt-4">
        <RegiaoDoPublico periodo={periodo} rotuloPeriodo={rotuloDoPeriodo(periodo)} />
      </div>
    </>
  );
}

function DashboardConteudo({
  data,
  loading,
  periodo,
}: {
  data: AnalyticsOverview | undefined;
  loading: boolean;
  periodo: Periodo;
}) {
  const tag = rotuloDoPeriodo(periodo);
  const totals = data?.totals;
  const deltas = data?.deltas;
  const attribution = data?.attribution;
  const tenure = data?.tenure;
  const { workspace } = useWorkspace();
  const breakdown = useAnalyticsBreakdown(workspace.id, periodo);
  /** `undefined` = carregando; `null` = a Meta não contou visita nenhuma (não é zero). */
  const pagina = breakdown.data?.pagina;

  /**
   * **A distinção que sustenta a tela inteira** (invariante 8).
   *
   * `observed.firstDay === null` quer dizer que este workspace nunca registrou um evento
   * sequer — nenhum monitorador entregou nada ainda. Nesse estado, TODO número é `null`, e
   * `null` vira travessão com a frase que diz o que falta fazer. Depois do primeiro evento,
   * silêncio passa a ser `0` medido, que é um número de verdade e aparece como número.
   *
   * Sem esta linha, um workspace recém-criado mostraria um painel inteiro de zeros — e um
   * painel de zeros é lido como "quebrado", não como "ainda não começou".
   */
  const semMedicao = data !== undefined && data.observed.firstDay === null;
  const valor = (n: number | undefined) => (semMedicao || n === undefined ? null : n);

  return (
    <>
      {semMedicao ? (
        <FormAlert tone="info" className="mb-3">
          Este workspace ainda não registrou nenhuma entrada ou saída. Os números aparecem assim que
          um monitorador estiver conectado e um grupo monitorado receber movimento — até lá,
          travessão significa “ainda não medimos”, e não “deu zero”.
        </FormAlert>
      ) : null}

      {/* Saldo é o único destaque da tela (tile violeta), como no kit. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          id="saldo"
          tone="brand"
          tag={tag}
          value={valor(totals?.balance)}
          delta={deltas?.balance ?? null}
          loading={loading}
        />
        <MetricTile
          id="entradas"
          tag={tag}
          value={valor(totals?.joins)}
          delta={deltas?.joins ?? null}
          loading={loading}
        />
        <MetricTile
          id="saidas"
          tag={tag}
          value={valor(totals?.leaves)}
          delta={deltas?.leaves ?? null}
          loading={loading}
        />
        <MetricTile
          id="taxaEntrada"
          tag={tag}
          value={attribution?.entryRate ?? null}
          loading={loading}
        />
      </div>

      {/*
        G2 (07/10): o destino de quem entrou no período, numa barra só. Os quatro números vêm
        prontos (`overview.fluxo`) e a partição fecha — ver metricas.md §4.1.
      */}
      <Card padded={false} className="mt-4 p-4">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          O que aconteceu com quem entrou · {tag}
        </p>
        <FlowBar
          loading={loading}
          entraram={semMedicao || !data ? null : data.fluxo.entraram}
          ficaram={data?.fluxo.ficaram ?? 0}
          sairam={data?.fluxo.sairam ?? 0}
          removidos={data?.fluxo.removidos ?? 0}
          semCausa={data?.fluxo.semCausa ?? 0}
          periodLabel={periodo === 'HOJE' ? 'hoje' : 'no período'}
        />
      </Card>

      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        {/*
          A página de ofertas (pedido do dono, 09/10) no lugar de remoções, saídas sem causa e
          órfãs — esses três contam limpeza e ruído do WhatsApp, não mudam nenhuma decisão.
        */}
        <MetricTile
          id="visitasPagina"
          size="compact"
          value={pagina === undefined ? null : (pagina?.visitas ?? null)}
          loading={loading || breakdown.isPending}
        />
        <MetricTile
          id="taxaCliquePagina"
          size="compact"
          value={pagina === undefined ? null : (pagina?.taxaDeClique ?? null)}
          loading={loading || breakdown.isPending}
        />
        <MetricTile
          id="cliques"
          size="compact"
          value={valor(attribution?.clicks)}
          delta={deltas?.clicks ?? null}
          loading={loading}
        />
        <MetricTile
          id="cliquesUnicos"
          size="compact"
          value={valor(attribution?.uniqueVisitors)}
          delta={deltas?.uniqueVisitors ?? null}
          loading={loading}
        />
      </div>

      <div className="mt-4">
        <SerieDiaria periodo={periodo} />
      </div>

      <FunilDoDashboard data={data} periodo={periodo} />

      {/*
        O lucro por lead (pedido do dono, em dinheiro) mora logo depois da série — antes vinha
        no topo, acima dos KPIs. Saiu de lá para a tela abrir como o UI kit do export: os
        números do funil primeiro, a comissão do dia em seguida. Nada nele mudou.

        `hoje` sai de `period.to` — o último dia do negócio do recorte, resolvido pelo
        SERVIDOR no fuso do workspace (às 22h em São Paulo o relógio do navegador já diz amanhã).
      */}
      <div className="mt-4">
        <LucroPorLeadPanel hoje={data?.period.to ?? null} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <CoberturaPanel attribution={attribution} joins={totals?.joins} loading={loading} />
        <PermanenciaPanel tenure={tenure} loading={loading} />
      </div>
    </>
  );
}

/* ========================================================================== */
/*  Série diária                                                               */
/* ========================================================================== */

function SerieDiaria({ periodo }: { periodo: Periodo }) {
  const { workspace } = useWorkspace();
  /*
   * No "Hoje" o gráfico mostra os dias ANTERIORES também (pedido do dono, 10/10): uma coluna
   * só ocupando a largura inteira não diz nada. Os outros recortes desenham o próprio período.
   */
  const recorte: Periodo = periodo === 'HOJE' ? 'D30' : periodo;
  const consulta = useAnalyticsOverview(workspace.id, recorte);
  const data = consulta.data;

  if (consulta.isPending || !data) {
    return (
      <Card>
        <CardHeader title="Entradas e saídas por dia" />
        <div className="h-52 animate-pulse rounded-md bg-muted" />
      </Card>
    );
  }

  const { observed } = data;
  const nenhumaMedicao = observed.firstDay === null;
  /*
   * Os dias antes do primeiro evento saem da frente do gráfico — 25 colunas hachuradas para
   * 5 medidas espremeriam o que importa. Fica sempre uma semana, no mínimo.
   */
  const primeiroMedido = data.series.findIndex((p) => p.measured);
  const corte =
    primeiroMedido < 0 ? 0 : Math.max(0, Math.min(primeiroMedido, data.series.length - 7));
  const series = data.series.slice(corte);

  return (
    <ChartCard
      title="Entradas e saídas por dia"
      description={
        periodo === 'HOJE' ? 'Os últimos dias, para comparar com hoje.' : 'Um ponto por dia.'
      }
      empty={
        nenhumaMedicao ? (
          <EmptyState
            kind="no-data"
            icon={Radio}
            title="Ainda não houve medição"
            description="Nenhum evento foi registrado neste workspace. Uma coluna em zero diria “ninguém entrou”, que é diferente de “não estávamos olhando” — por isso o gráfico não é desenhado."
          />
        ) : undefined
      }
      table={
        <Table caption="Entradas, saídas e saldo por dia" density="dense">
          <TableHeader>
            <TableRow>
              <TableHead>Dia</TableHead>
              <TableHead>Entradas</TableHead>
              <TableHead>Saídas</TableHead>
              <TableHead>Saldo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...series].reverse().map((p) => (
              <TableRow key={p.date}>
                <TableRowHeader>{diaLongo(p.date)}</TableRowHeader>
                {p.measured ? (
                  <>
                    <TableCell numeric>{formatNumber(p.joins)}</TableCell>
                    <TableCell numeric>{formatNumber(p.leaves)}</TableCell>
                    <TableCell numeric>{formatNumber(p.balance)}</TableCell>
                  </>
                ) : (
                  /*
                   * Três travessões, e não três zeros: este dia é anterior ao primeiro evento
                   * do workspace. É a mesma informação que a hachura do gráfico, em texto —
                   * e é a única leitura possível para quem usa leitor de tela.
                   */
                  <TableCell colSpan={3} className="text-muted-foreground">
                    sem medição
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    >
      <DailyBars series={series} />
    </ChartCard>
  );
}

/* ========================================================================== */
/*  Cobertura de atribuição — critério de aceite da fase                       */
/* ========================================================================== */

/**
 * A cobertura de atribuição, **visível mesmo quando não há o que atribuir**.
 *
 * Este painel não some com zero clique, e isso é o critério de aceite da Fase 6 levado ao pé
 * da letra: "cobertura de atribuição visível". Esconder o painel quando não há dado deixaria
 * quem instalou o produto sem saber que existe um número para acompanhar — e ele só ia
 * descobrir no dia em que o número já estivesse ruim.
 *
 * `coverage === null` não é `0%`: quer dizer que não houve entrada no período. A frase muda
 * conforme o motivo, porque os motivos mandam fazer coisas diferentes.
 */
function CoberturaPanel({
  attribution,
  joins,
  loading,
}: {
  attribution: AnalyticsOverview['attribution'] | undefined;
  joins: number | undefined;
  loading: boolean;
}) {
  if (loading || !attribution) {
    return (
      <Card>
        <CardHeader title="Cobertura de atribuição" />
        <div className="h-24 animate-pulse rounded-md bg-muted" />
      </Card>
    );
  }

  const semClique = attribution.clicks === 0;
  const semEntrada = (joins ?? 0) === 0;

  return (
    <Card>
      <CardHeader
        title="Cobertura de atribuição"
        description="De quanto do seu dado a gente sabe a origem. É saúde do sistema, não desempenho de campanha."
      />

      <div className="grid grid-cols-2 gap-3">
        <MetricTile id="cobertura" size="compact" value={attribution.coverage} />
        <MetricTile id="taxaEntrada" size="compact" value={attribution.entryRate} />
      </div>

      {semEntrada ? (
        /*
         * `coverage` é `null`: não houve entrada no período, então não existe denominador e
         * não há o que atribuir. O tile acima já mostra travessão; aqui vai o porquê.
         */
        <FormAlert tone="info" className="mt-3">
          Nenhuma entrada foi registrada no período, então{' '}
          <strong>ainda não há o que atribuir</strong>. Isso não é 0% de cobertura: é a ausência do
          que seria medido. A cobertura passa a existir quando um grupo monitorado receber a
          primeira entrada.
        </FormAlert>
      ) : semClique ? (
        /*
         * **O caso que a tela errava.** Com entradas e zero clique, `coverage` é `0` — e é um
         * ZERO MEDIDO, não ausência: nós temos as entradas e não sabemos a origem de nenhuma
         * delas. A versão anterior mostrava "0,0%" no tile e, logo abaixo, a frase "isso não é
         * 0% de cobertura" — a tela se contradizendo em dois centímetros. Quem lesse escolheria
         * uma das duas, e metade escolheria errado.
         */
        <FormAlert tone="warning" className="mt-3">
          {formatNumber(joins ?? 0)} entrada(s) no período e <strong>nenhum clique</strong> nos seus
          redirecionadores. Então não sabemos a origem de nenhuma delas:{' '}
          <strong>este 0% é medido</strong>, não é falta de dado. Ou as pessoas estão entrando por
          um caminho que não passa pelo seu link, ou o link ainda não foi para o ar.
        </FormAlert>
      ) : (
        <>
          <Table caption="Cliques por origem no período" density="dense" className="mt-3">
            <TableHeader>
              <TableRow>
                <TableHead>Origem</TableHead>
                <TableHead>Cliques</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableRowHeader>Veio de anúncio (Meta)</TableRowHeader>
                <TableCell numeric>{formatNumber(attribution.metaAds)}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader>Marcado à mão (utm)</TableRowHeader>
                <TableCell numeric>{formatNumber(attribution.tagged)}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader>Sem origem</TableRowHeader>
                <TableCell numeric>{formatNumber(attribution.noOrigin)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {attribution.noOrigin > 0 ? (
            <FormAlert tone="warning" className="mt-3">
              {formatNumber(attribution.noOrigin)} de {formatNumber(attribution.clicks)} cliques
              chegaram sem nenhum parâmetro de origem. Quase sempre é o botão da sua página com um
              link fixo: abra Redirecionadores para ver o trecho de instalação.
            </FormAlert>
          ) : null}
        </>
      )}

      {!semClique && semEntrada ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Houve clique e nenhuma entrada no período, então a cobertura fica sem denominador —
          travessão, não 0%.
        </p>
      ) : null}
    </Card>
  );
}

/* ========================================================================== */
/*  Permanência — e o que fazer quando ela é DESCONHECIDA                      */
/* ========================================================================== */

/**
 * A permanência, com a honestidade que o dado de hoje exige.
 *
 * A varredura de baseline grava `joined_at = null` de propósito (ADR-0014): carimbar `now()`
 * inventaria uma entrada de hoje para quem está no grupo desde março. A consequência é que,
 * para todo mundo que já estava lá quando o monitorador entrou, **a permanência é
 * desconhecida, não zero** — e é isso que este painel escreve em português.
 *
 * O número de pessoas com entrada desconhecida aparece do lado da mediana, sempre. Sem ele, a
 * mediana pareceria falar do grupo inteiro; com ele, fica claro sobre quantas pessoas ela
 * realmente fala. É a diferença entre uma medição e uma impressão.
 */
function PermanenciaPanel({
  tenure,
  loading,
}: {
  tenure: AnalyticsOverview['tenure'] | undefined;
  loading: boolean;
}) {
  if (loading || !tenure) {
    return (
      <Card>
        <CardHeader title="Permanência" />
        <div className="h-24 animate-pulse rounded-md bg-muted" />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Permanência"
        description="Quanto tempo as pessoas ficam no grupo, entre as que já saíram."
      />

      <div className="grid grid-cols-2 gap-3">
        <MetricTile
          id="meiaVida"
          size="compact"
          value={tenure.medianDays}
          suffix={tenure.medianDays === null ? undefined : ' d'}
          footer={
            tenure.medianDays !== null && tenure.minDays !== null && tenure.maxDays !== null ? (
              <span className="nums text-2xs text-muted-foreground">
                de {diasEmTexto(tenure.minDays)} a {diasEmTexto(tenure.maxDays)}
              </span>
            ) : null
          }
        />
        <StatTile
          size="compact"
          label="Sobre quantas pessoas"
          value={tenure.knownLeaves}
          abbreviate="never"
          footer={
            <span className="text-2xs text-subtle-foreground">
              saídas com entrada registrada no período
            </span>
          }
        />
      </div>

      {/*
        O bloco que o dado real de hoje exige: 550 pessoas no grupo varrido, TODAS com
        `joined_at` nulo. A permanência delas não é 0 — ela não existe, e a tela diz isso.
      */}
      {tenure.unknownEntry > 0 || tenure.orphanLeaves > 0 ? (
        <FormAlert tone="info" className="mt-3">
          <span className="block font-medium">Permanência desconhecida</span>
          {tenure.unknownEntry > 0 ? (
            <span className="mt-1 block">
              {formatNumber(tenure.unknownEntry)} pessoas estão nos grupos sem data de entrada: elas
              já estavam lá quando o monitorador entrou. Registrar a data de hoje para elas
              inventaria uma entrada que não houve — por isso a permanência delas é{' '}
              <strong>desconhecida, não zero</strong>, e nenhuma delas entra na mediana acima.
            </span>
          ) : null}
          {tenure.orphanLeaves > 0 ? (
            <span className="mt-1 block">
              {formatNumber(tenure.orphanLeaves)} saída(s) do período não tinham entrada registrada
              (saídas órfãs). Elas contam como saída, mas não têm quanto tempo medir.
            </span>
          ) : null}
        </FormAlert>
      ) : null}
    </Card>
  );
}

/**
 * O dinheiro das campanhas no topo do Dashboard (pedido do dono, 06/10): investimento, CPL
 * Meta, CPL real e entradas no MESMO período dos números abaixo. Só aparece quando há gasto —
 * quem não anuncia não precisa de seis traços na primeira tela.
 */
function CampanhasNoPeriodo({ periodo }: { periodo: Periodo }) {
  const { workspace } = useWorkspace();
  const breakdown = useAnalyticsBreakdown(workspace.id, periodo);
  const funil = breakdown.data?.funil;
  if (!funil || !funil.some((r) => r.custos.gastoCentavos !== null)) return null;
  return (
    <section aria-label="Campanhas no período" className="mb-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="flex flex-wrap items-baseline gap-x-3 text-sm font-semibold text-foreground">
          Campanhas no período
        </h2>
        <Link
          to={workspacePath(workspace.slug, 'campanhas')}
          className="text-sm font-medium text-primary-text underline-offset-2 hover:underline"
        >
          Ver por campanha
        </Link>
      </div>
      <CampaignTotals rows={funil} />
    </section>
  );
}

function FunilDoDashboard({
  data,
  periodo,
}: {
  data: AnalyticsOverview | undefined;
  periodo: Periodo;
}) {
  const { workspace } = useWorkspace();
  const breakdown = useAnalyticsBreakdown(workspace.id, periodo);
  return (
    <FunilDoPeriodo overview={data} funil={breakdown.data?.funil} pagina={breakdown.data?.pagina} />
  );
}
